import React, { useState } from "react";
import { Download } from "@mui/icons-material";
import { Box } from "@/Components/v2/atoms/Box";
import { Breadcrumbs } from "@/Components/v2/atoms/Breadcrumbs";
import { Stack } from "@/Components/v2/atoms/Stack";
import { Divider } from "@/Components/v2/atoms/Divider";
import { LoadingButton, PrimaryButton } from "@/Components/v2/atoms/Button";
import { Typography } from "@/Components/v2/atoms/Typography";
import MetaField from "@/Components/v2/molecule/MetaField/MetaField";
import { useActionPlanResultsContext } from "./ActionPlanResultsProvider";
import dayjs from "dayjs";
import { formatDateLocale } from "@/shared/utils/formatDate";
import StatusLabel from "./components/StatusLabel";
import StatusModal from "./components/StatusModal";
import ActionPlanLoadingState from "./components/ActionPlanLoadingState";
import { ActionPlanStatus } from "@mutiny-group/sbo-openapi-client";
import { useDownloadSpendingPlan } from "@/pages/scenario-builder/v3/api/v2/getSpendingPlan";
import { useScenariosV3Routes } from "@/pages/scenario-builder/v3/hooks/useScenariosV3Routes";
import { useGeoEnabled } from "@/pages/scenario-builder/v3/hooks/useGeoEnabled";

interface ActionPlanResultsLayoutProps {
  children: React.ReactNode;
}

export const ActionPlanResultsLayout = ({ children }: ActionPlanResultsLayoutProps) => {
  const { actionPlan, isActionPlanLoading, isActionPlanFetching } = useActionPlanResultsContext();
  const isLoading = isActionPlanLoading || isActionPlanFetching;
  const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);
  const geoEnabledQuery = useGeoEnabled();
  const isGeoEnabled = geoEnabledQuery.isLoading ? false : geoEnabledQuery.isEnabled;

  const { mutate: downloadSpendingPlan, isPending: isSpendingPlanLoading } =
    useDownloadSpendingPlan({
      actionPlan: actionPlan!,
    });

  const { navigateToActionPlans } = useScenariosV3Routes();

  if (isLoading) {
    return <ActionPlanLoadingState />;
  }

  if (!actionPlan) {
    return (
      <Box margin="auto" maxWidth={650} my={14}>
        <Stack direction="column" gap={2}>
          <Typography fontSize={28} fontWeight={600}>
            Action Plan Not Found
          </Typography>
          <Typography variant="body1" color="text.secondary">
            We&apos;re sorry, we couldn&apos;t load this Action Plan. This usually happens if the
            plan was recently deleted, or if there was a temporary network issue.
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Try refreshing the page, or go back to view all your available Action Plans.
          </Typography>
          <Box sx={{ marginTop: 2 }}>
            <PrimaryButton onClick={navigateToActionPlans}>View All Action Plans</PrimaryButton>
          </Box>
        </Stack>
      </Box>
    );
  }

  const { name, scopeBrandForDisplay, scopeGeographies, updatedAt, startDate, endDate, status } =
    actionPlan;

  const geographyValue =
    scopeGeographies?.length && scopeGeographies.length > 0
      ? scopeGeographies.join(", ")
      : "All Geographies";

  const metaData = [
    `Business Unit: ${scopeBrandForDisplay ?? "-"}`,
    ...(isGeoEnabled ? [`Geography: ${geographyValue}`] : []),
    `${formatDateLocale(dayjs(startDate), { dateFormat: "DD MMM YYYY" })} - ${formatDateLocale(dayjs(endDate), { dateFormat: "DD MMM YYYY" })}`,
  ];

  const handleDownloadSpendingPlan = () => {
    downloadSpendingPlan();
  };

  const handleStatusLabelClick = () => setIsStatusModalOpen(true);
  const statusLabelOnClick =
    status !== ActionPlanStatus.Complete ? handleStatusLabelClick : undefined;

  return (
    <Box sx={{ paddingX: 4, paddingTop: 3, paddingBottom: 8 }}>
      <Stack spacing={3}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Breadcrumbs
            items={[{ label: "Action Plans", to: "/scenarios-v3/action-plans" }, { label: name }]}
          />
          <LoadingButton
            startIcon={<Download />}
            loading={isSpendingPlanLoading}
            loadingPosition="start"
            onClick={handleDownloadSpendingPlan}
            variant="contained"
            color="primary"
          >
            Spending Plan
          </LoadingButton>
        </Stack>
        <Divider sx={{ marginBottom: "1.5rem" }} />
        <Stack direction="row" justifyContent="space-between" alignItems="center" borderRadius={4}>
          <Stack direction="row" spacing={1} alignItems="center">
            {metaData.map((value, index) => (
              <MetaField value={value} key={index} />
            ))}
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center">
            <MetaField
              title="Last Updated:"
              value={
                updatedAt
                  ? formatDateLocale(dayjs(updatedAt), {
                      dateFormat: "DD MMM YYYY",
                    })
                  : "N/A"
              }
            />
            <StatusLabel status={status} onClick={statusLabelOnClick} />
          </Stack>
        </Stack>
        <Box sx={{ marginTop: "0.5rem" }}>{children}</Box>
      </Stack>
      <StatusModal open={isStatusModalOpen} onClose={() => setIsStatusModalOpen(false)} />
    </Box>
  );
};
